/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate, Link } from 'react-router-dom';
import { PlusCircle, Edit3, Trash2, Save, XCircle, AlertCircle, CheckCircle, Loader2 } from 'lucide-react';

function AdminLanguagesPage() {
    const [languages, setLanguages] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');
    const navigate = useNavigate();
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    const token = localStorage.getItem('adminToken');

    const [isAdding, setIsAdding] = useState(false);
    const [newLanguage, setNewLanguage] = useState({ code: '', name: '', is_active: true });
    const [editingLanguage, setEditingLanguage] = useState(null); // { code: string, name: string, is_active: boolean }
    const [formError, setFormError] = useState('');
    const [isProcessing, setIsProcessing] = useState(false);

    const fetchLanguages = useCallback(async () => {
        setIsLoading(true); setError('');
        if (!token) { setError('Brak autoryzacji.'); setIsLoading(false); navigate('/admin/login'); return; }
        try {
            const response = await axios.get(`${apiUrl}/api/admin/languages`, { headers: { 'x-auth-token': token } });
            setLanguages(response.data);
        } catch (err) { setError(err.response?.data?.errors?.[0]?.msg || 'Nie udało się pobrać listy języków.'); if (err.response?.status === 401 || err.response?.status === 403) { navigate('/admin/login');} }
        finally { setIsLoading(false); }
    }, [apiUrl, navigate, token]);

    useEffect(() => { fetchLanguages(); }, [fetchLanguages]);

    const handleNewLangChange = (e) => {
        const { name, value, type, checked } = e.target;
        setNewLanguage(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        setFormError('');
    };

    const handleEditLangChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditingLanguage(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
        setFormError('');
    };

    const handleAddNewLanguage = async (e) => {
        e.preventDefault(); setFormError(''); setIsProcessing(true);
        if (!newLanguage.code || !newLanguage.name) { setFormError('Kod i nazwa języka są wymagane.'); setIsProcessing(false); return; }
        try {
            await axios.post(`${apiUrl}/api/admin/languages`, newLanguage, { headers: { 'x-auth-token': token } });
            setNewLanguage({ code: '', name: '', is_active: true }); setIsAdding(false); fetchLanguages();
        } catch (err) { setFormError(err.response?.data?.errors?.[0]?.msg || 'Nie udało się dodać języka.'); }
        finally { setIsProcessing(false); }
    };

    const handleUpdateLanguage = async (e) => {
        e.preventDefault(); setFormError(''); setIsProcessing(true);
        if (!editingLanguage || !editingLanguage.name) { setFormError('Nazwa języka jest wymagana.'); setIsProcessing(false); return; }
        try {
            await axios.put(`${apiUrl}/api/admin/languages/${editingLanguage.code}`, { name: editingLanguage.name, is_active: editingLanguage.is_active }, { headers: { 'x-auth-token': token } });
            setEditingLanguage(null); fetchLanguages();
        } catch (err) { setFormError(err.response?.data?.errors?.[0]?.msg || 'Nie udało się zaktualizować języka.'); }
        finally { setIsProcessing(false); }
    };

    const handleDeleteLanguage = async (code, name) => {
        if (!window.confirm(`Czy na pewno chcesz usunąć język "${name}" (kod: ${code})? Tej operacji nie można cofnąć.`)) return;
        setIsProcessing(true); setFormError(''); // Ustawienie dla konkretnego wiersza, trudniejsze
        try {
            await axios.delete(`${apiUrl}/api/admin/languages/${code}`, { headers: { 'x-auth-token': token } });
            fetchLanguages();
        } catch (err) { alert(err.response?.data?.errors?.[0]?.msg || 'Nie udało się usunąć języka.'); } // Użyj alertu dla prostoty
        finally { setIsProcessing(false); }
    };

    const startEdit = (lang) => { setEditingLanguage({ ...lang }); setIsAdding(false); setFormError(''); };
    const cancelEdit = () => { setEditingLanguage(null); setFormError(''); };
    const startAdd = () => { setIsAdding(true); setNewLanguage({ code: '', name: '', is_active: true }); setEditingLanguage(null); setFormError(''); };
    const cancelAdd = () => { setIsAdding(false); setFormError(''); };

    if (isLoading) return <div className="container" style={{paddingTop: '2rem'}}>Ładowanie języków...</div>;
    if (error && languages.length === 0) return <div className="container submit-message error" style={{paddingTop: '2rem'}}>{error}</div>;

    return (
        <section className="admin-page section-bg-white" style={{padding: "40px 0", minHeight: 'calc(100vh - var(--header-height) - var(--footer-height, 150px))'}}>
            <div className="container">
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem'}}>
                    <h1>Panel Administratora - Zarządzanie Językami</h1>
                    <Link to="/admin/dashboard" className="button-secondary button-small-outline">← Wróć do Dashboardu</Link>
                </div>

                {!isAdding && !editingLanguage && (
                    <button onClick={startAdd} className="button-primary" style={{marginBottom: '1.5rem'}}>
                        <PlusCircle size={18} style={{marginRight: '0.5rem'}}/> Dodaj Nowy Język
                    </button>
                )}

                {(isAdding || editingLanguage) && (
                    <form onSubmit={editingLanguage ? handleUpdateLanguage : handleAddNewLanguage} className="admin-edit-form" style={{marginBottom: '2rem'}}>
                        <h3>{editingLanguage ? `Edytuj Język: ${editingLanguage.name}` : 'Dodaj Nowy Język'}</h3>
                        {formError && <div className="submit-message error form-message"><AlertCircle size={18}/> {formError}</div>}
                        <div className="form-group">
                            <label htmlFor="langCode">Kod Języka (np. en, de, zh-CN)</label>
                            <input type="text" id="langCode" name="code" value={editingLanguage ? editingLanguage.code : newLanguage.code} onChange={editingLanguage ? handleEditLangChange : handleNewLangChange} required maxLength="10" disabled={!!editingLanguage || isProcessing} />
                        </div>
                        <div className="form-group">
                            <label htmlFor="langName">Pełna Nazwa Języka</label>
                            <input type="text" id="langName" name="name" value={editingLanguage ? editingLanguage.name : newLanguage.name} onChange={editingLanguage ? handleEditLangChange : handleNewLangChange} required maxLength="100" disabled={isProcessing} />
                        </div>
                        <div className="form-group form-consent" style={{flexDirection: 'row', alignItems: 'center'}}>
                            <input type="checkbox" id="langIsActive" name="is_active" checked={editingLanguage ? editingLanguage.is_active : newLanguage.is_active} onChange={editingLanguage ? handleEditLangChange : handleNewLangChange} disabled={isProcessing} style={{width: 'auto', marginRight: '0.5rem'}} />
                            <label htmlFor="langIsActive" style={{marginBottom: 0}}>Aktywny (widoczny dla klientów)</label>
                        </div>
                        <div style={{marginTop: '1rem', display: 'flex', gap: '1rem'}}>
                            <button type="submit" className="button-primary" disabled={isProcessing}>
                                {isProcessing ? <Loader2 size={18} className="spinner"/> : <Save size={18}/>}
                                {editingLanguage ? 'Zapisz Zmiany' : 'Dodaj Język'}
                            </button>
                            <button type="button" onClick={editingLanguage ? cancelEdit : cancelAdd} className="button-secondary" disabled={isProcessing}>Anuluj</button>
                        </div>
                    </form>
                )}

                {error && languages.length > 0 && <div className="submit-message error" style={{ marginBottom: '1rem'}}>{error}</div>}
                
                <div className="table-responsive-wrapper">
                    <table className="admin-table">
                        <thead><tr><th>Kod</th><th>Nazwa</th><th>Aktywny</th><th>Data Utworzenia</th><th>Akcje</th></tr></thead>
                        <tbody>
                            {languages.map(lang => (
                                <tr key={lang.code}>
                                    <td>{lang.code}</td><td>{lang.name}</td>
                                    <td><span className={`status-badge ${lang.is_active ? 'status-completed' : 'status-rejected'}`}>{lang.is_active ? 'Tak' : 'Nie'}</span></td>
                                    <td>{new Date(lang.created_at).toLocaleDateString('pl-PL')}</td>
                                    <td>
                                        <button onClick={() => startEdit(lang)} className="button-small-outline" style={{marginRight: '0.5rem'}} disabled={isProcessing}><Edit3 size={14}/> Edytuj</button>
                                        <button onClick={() => handleDeleteLanguage(lang.code, lang.name)} className="button-small-outline button-danger" disabled={isProcessing}><Trash2 size={14}/> Usuń</button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </section>
    );
}
export default AdminLanguagesPage;